import * as React from 'react'
import type { AppStateType } from '../../reducers/appstate'
import type { Dispatch } from 'redux'
import { withRouter } from 'next/router'
import { connect } from 'react-redux'
import { PromoApplied } from './PromoApplied'
import { InputWithApplyCta } from './InputWithApplyCta'
import {
  applyPromoCode,
  clearPromoCode
} from '../../actions/subscriptionActions'
import type { SubscriptionLengthType } from '../../flowTypes'
import { Form } from '../shared'
import { colors } from '@components/shared/colors'
import styled from 'styled-components'
import { config } from 'react-awesome-styled-grid'

type PropsType = {
  validPromo?: boolean,
  plan?: SubscriptionLengthType,
  promoText?: string,
  promoError?: string,
  userPromo?: string,
  userEnteredPromo?: string,
  isGiftPromo?: boolean,
  isGift: boolean,
  theme: 'white' | 'sand',
  containerStyles?: {},
  router: {
    query: {
      promo?: string
    }
  },
  bottomBorder: boolean,
  dontDisplayPromo?: boolean
}

type DispatchPropsType = {
  applyPromoCode: (userEnteredPromo: string, isGift: boolean) => void,
  clearPromo: (isGift: boolean) => void
}

type StateType = {
  userEnteredPromo?: string,
  submitted: boolean,
  urlPromo?: string
}

class PromoCode extends React.Component<
  PropsType & DispatchPropsType,
  StateType
> {
  state: StateType = {
    userEnteredPromo: '',
    submitted: false
  }

  constructor (props: PropsType & DispatchPropsType) {
    super(props)
    const { promo } = this.props.router.query
    if (promo) {
      this.state.userEnteredPromo = promo
    }
  }

  componentDidMount (): void {
    let urlPromo
    const { promo } = this.props.router.query
    if (this.props.isGift && !this.props.isGiftPromo && this.props.promoText) {
      this.props.clearPromo(this.props.isGift)
    }
    if (window && !promo) {
      const params = new URLSearchParams(window?.location?.search)
      if (params.has('promo')) {
        urlPromo = params.get('promo')
      }
    } else if (promo) {
      urlPromo = promo
      const promoValue = promo as string | string[]
      if (typeof promoValue !== 'string' && promoValue.length > 1) {
        urlPromo = promoValue[promoValue.length - 1]
      }
    }
    if (urlPromo) {
      this.setState({
        urlPromo
      })
      this.props.applyPromoCode(urlPromo, this.props.isGift)
    }
  }

  componentDidUpdate (
    prevProps: PropsType & DispatchPropsType,
    prevState: StateType
  ): void {
    if (this.props.plan !== prevProps.plan) {
      if (this.state.userEnteredPromo) {
        this.props.applyPromoCode(
          this.state.userEnteredPromo,
          this.props.isGift
        )
      }
    }
    let urlPromo
    const { promo } = this.props.router.query
    if (window && !promo) {
      const params = new URLSearchParams(window?.location?.search)
      if (params.has('promo')) {
        urlPromo = params.get('promo')
      }
    } else if (promo) {
      urlPromo = promo
    }
    if (
      urlPromo &&
      this.state.userEnteredPromo !== urlPromo &&
      this.state.urlPromo !== prevState.urlPromo
    ) {
      this.props.applyPromoCode(urlPromo, this.props.isGift)
    }
  }

  applyPromo = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault()

    if (this.state.userEnteredPromo) {
      this.props.applyPromoCode(this.state.userEnteredPromo, this.props.isGift)
      this.setState({
        submitted: true
      })
    }
  }

  clearPromo = (): void => {
    this.setState({
      userEnteredPromo: '',
      submitted: false
    })
    this.props.clearPromo(this.props.isGift)
  }

  updatePromo = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({ userEnteredPromo: e.target.value })
  }

  renderPromoSection (): React.ReactNode {
    const {
      validPromo,
      promoError,
      userPromo,
      theme,
      containerStyles
    } = this.props
    let content = null

    if (validPromo && userPromo) {
      content = (
        <PromoApplied
          promoCode={userPromo}
          textColor={theme === 'sand' ? colors.sand : colors.white}
          checkColor={colors.midnightBlue}
          checkBackgroundColor={colors.orange}
          clearPromo={this.clearPromo}
        />
      )
    } else {
      content = (
        <InputWithApplyCta
          clearPromo={this.clearPromo}
          name='Promo Code'
          placeholder='Promo Code'
          errorMessage={promoError}
          onClickApply={this.applyPromo}
          onChange={this.updatePromo}
          formId={'promoForm'}
          color={theme === 'sand' ? colors.sand : colors.white}
        />
      )
    }

    return (
      <PromoGift
        color={theme === 'sand' ? colors.sand : colors.white}
        style={containerStyles}
        $borderBottom={this.props.bottomBorder}
      >
        {content}
      </PromoGift>
    )
  }

  render (): React.ReactNode {
    if (this.props.dontDisplayPromo && this.state.urlPromo) {
      return null
    }
    return (
      <Form id='promoForm' onSubmit={this.applyPromo}>
        {this.renderPromoSection()}
      </Form>
    )
  }
}

const mapStateToProps = (
  state: AppStateType,
  ownProps: { isGift?: boolean, theme: 'white' | 'sand', borderBottom: boolean }
): { borderBottom: any, validPromo: any, plan: any, promoText: any, promoError: any, userPromo: any, isGift: any, theme: any, isGiftPromo: any } => {
  return {
    borderBottom: ownProps.borderBottom !== false,
    validPromo: state.subscriptionPromo
      ? state.subscriptionPromo.valid
      : undefined,
    plan: (state.subscriptionPurchase || {}).plan,
    promoText: (state.subscriptionText || {}).promoText,
    promoError: (state.subscriptionText || {}).promoError,
    userPromo: state.subscriptionPromo
      ? state.subscriptionPromo.userEnteredPromo
      : undefined,
    isGift: ownProps.isGift || false,
    theme: ownProps.theme,
    isGiftPromo: state.subscriptionPromo
      ? state.subscriptionPromo.isAvailableOnGift
      : false
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): any => {
  return {
    applyPromoCode: (promoCode: string, isGift: boolean): void =>
      dispatch(applyPromoCode(promoCode, isGift)),
    clearPromo: (isGift: boolean): void => dispatch(clearPromoCode(isGift))
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(PromoCode) as any
) as any

const PromoGift = styled.div<any>`
  ${(props: any): string => {
    if (props.$borderBottom) {
      return `border-bottom: 1px solid ${props.color || colors.sand};`
    }
    return ''
  }};
  display: flex;
  flex-direction: column;
  padding: 0 0 5px;
  width: 100%;
  ${(props: any): any => config(props).media.sm`
    border: none;
  `}
`
